import React from "react";
import { createPortal } from "react-dom";
import { Box, Typography } from "@mui/material";
import { RefreshCw, Square } from "lucide-react";
import type { LiveSpeechSessionError } from "../../types/liveSpeechSessionError";
import { MicOnIcon } from "../../assets/icon";
import { useAppStore } from "../../stores/appStore";
import {
  overlayStyles,
  panelStyles,
  waveDecorStyles,
  contentStyles,
  iconWrapStyles,
  titleStyles,
  subtitleStyles,
  actionsRowStyles,
  actionItemStyles,
  actionCircleStyles,
  actionLabelStyles,
  type ActionTone,
} from "./styles";

interface LiveSpeechSessionErrorModalProps {
  error: LiveSpeechSessionError | null;
  onRetry?: () => void;
  onFallbackAudioOnly?: () => void;
  onExit: () => void;
}

function splitSessionErrorMessage(message: string): {
  title: string;
  subtitle?: string;
} {
  const trimmed = message.trim();

  if (trimmed === "Please start speaking to begin the conversation.") {
    return {
      title: "Please start speaking",
      subtitle: "to begin the conversation.",
    };
  }

  const match = trimmed.match(/^([^.!?]+[.!?])\s+(.+)$/s);
  if (match) {
    return {
      title: match[1].replace(/[.!?]+$/, "").trim(),
      subtitle: match[2].trim(),
    };
  }

  return { title: trimmed };
}

const WAVE_BAR_HEIGHTS = [10, 18, 28, 34, 28, 18, 10] as const;

const FOOTER_MIC_GRADIENT = "linear-gradient(180deg, #28C9DC 0%, #7B49C1 100%)";

const WaveformIcon: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "3.5px",
      height: { xs: 32, sm: 36 },
    }}
    aria-hidden
  >
    {WAVE_BAR_HEIGHTS.map((height, index) => (
      <Box
        key={index}
        sx={{
          width: 4.5,
          height,
          borderRadius: 999,
          flexShrink: 0,
          background: FOOTER_MIC_GRADIENT,
        }}
      />
    ))}
  </Box>
);

interface ActionButtonProps {
  label: string;
  tone: ActionTone;
  isDark: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  label,
  tone,
  isDark,
  onClick,
  icon,
}) => (
  <Box component="button" type="button" onClick={onClick} sx={actionItemStyles}>
    <Box sx={actionCircleStyles(isDark, tone)}>{icon}</Box>
    <Typography component="span" sx={actionLabelStyles(isDark)}>
      {label}
    </Typography>
  </Box>
);

export const LiveSpeechSessionErrorModal: React.FC<
  LiveSpeechSessionErrorModalProps
> = ({ error, onRetry, onFallbackAudioOnly, onExit }) => {
  const isDark = useAppStore((s) => s.isDark);

  if (!error) return null;

  const showRetry = error.retryable && Boolean(onRetry);
  const showAudioOnly =
    error.canFallbackAudioOnly && Boolean(onFallbackAudioOnly);
  const exitLabel =
    error.code === "week_limit_exceeded" ? "Close session" : "End session";
  const { title, subtitle } = splitSessionErrorMessage(error.message);

  return createPortal(
    <Box sx={overlayStyles}>
      <Box sx={panelStyles(isDark)}>
        <Box sx={waveDecorStyles(isDark)} />
        <Box sx={contentStyles}>
          <Box sx={iconWrapStyles}>
            <WaveformIcon />
          </Box>

          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Typography sx={titleStyles(isDark)}>{title}</Typography>
            {subtitle ? (
              <Typography sx={subtitleStyles(isDark)}>{subtitle}</Typography>
            ) : null}
          </Box>

          <Box sx={actionsRowStyles}>
            {showAudioOnly && (
              <ActionButton
                label="Continue audio only"
                tone="primary"
                isDark={isDark}
                onClick={onFallbackAudioOnly!}
                icon={<MicOnIcon size={22} />}
              />
            )}
            {showRetry && (
              <ActionButton
                label="Retry"
                tone="neutral"
                isDark={isDark}
                onClick={onRetry!}
                icon={<RefreshCw size={22} strokeWidth={2.4} />}
              />
            )}
            <ActionButton
              label={exitLabel}
              tone="danger"
              isDark={isDark}
              onClick={onExit}
              icon={
                <Square
                  size={18}
                  fill="currentColor"
                  strokeWidth={0}
                  absoluteStrokeWidth
                />
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>,
    document.body,
  );
};
